<template>
	<q-page padding>

		<div v-if="hasCourses">
			<transition-group enter-active-class="animated bounceInLeft" appear>
				<q-alert
					key="1"
					v-if="isError"
					color="negative"
					icon="error"
					appear
					class="q-mb-sm">
					<div>
						An error occurred and we were unable to find your transferable courses.
					</div>
				</q-alert>

				<div key=2 v-if="isError">
					<q-btn color="primary" icon="cached" @click="retry" label="Retry" />
				</div>
			</transition-group>

			<div v-if="isLoading">
				<p class="caption">
					<q-spinner color="primary" style="margin-left: 1rem;" />
					Loading, please wait &hellip;
				</p>
			</div>

			<div v-if="!isLoading">
				<EquivalencyMap v-if="hasCourses" />

				<MarkdownText v-if="showNextStepsText" :value="nextStepsText"></MarkdownText>
			</div>
		</div>

		<div v-if="!hasCourses">
			<q-alert
				color="secondary"
				icon="playlist_add_check"
				appear
				class="q-mb-sm"
				>
				Please enter at least one course or assessment to see how your courses will transfer.
			</q-alert>

			<q-btn icon="class" label="My Courses" color="primary" @click="$router.push({ name: 'my-courses' })" class="q-mr-md" />
			<q-btn icon="assessment" label="My Assessments" color="primary" @click="$router.push({ name: 'my-assessments' })" />
		</div>


	</q-page>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { mapGetters, mapActions } from 'vuex';
import { client as collegeTransferClient, IWillMyCreditsTransferResponse } from '@/models/services/collegetransfer/';
import { IWmctMapItem, IWillMyCreditsTransferRequest } from '@/models/services/collegetransfer/entities';
import { State } from '@/store';
import { WillMyCreditsTransferState } from '@/store/wmct/state';
import EquivalencyMap from '@/components/equivalency-map/EquivalencyMap.vue';
import MarkdownText from '@/components/MarkdownText.vue';

@Component({
	computed: mapGetters('wmct', [
		'equivalencyRequestPayload',
	]),
	methods: mapActions('wmct', [
		'updateSeenEquivalencies',
		'runEquivalencyMap',
	]),
	components: {
		EquivalencyMap,
		MarkdownText,
	},
})
export default class WillMyCreditsTransfer extends Vue {
	private equivalencyRequestPayload!: IWillMyCreditsTransferRequest;
	private updateSeenEquivalencies!: () => void;
	private runEquivalencyMap!: () => Promise<void>;

	private get state(): WillMyCreditsTransferState {
		return (this.$store.state as State).wmct;
	}

	public get nextStepsText() {
		if (this.state.api.data) {
			return this.state.api.data.nextStepsText;
		}
		return null;
	}

	public get showNextStepsText() {
		return this.hasCourses && this.nextStepsText != null;
	}

	public get hasCourses() {
		return this.equivalencyRequestPayload.TakenCourses.length > 0;
	}

	public get equivalencyMap() {
		return this.$store.state.wmct.equivalencyMap;
	}

	public get isLoading() {
		return this.$store.state.wmct.equivalencyMapLoading;
	}

	public get isError() {
		return this.$store.state.wmct.equivalencyMapError;
	}

	public async retry() {
		await this.runEquivalencyMap();

		this.updateSeenEquivalencies();
	}

	public async mounted() {
		window.scrollTo(0, 0);

		this.updateSeenEquivalencies();

		if (this.hasCourses && (!this.equivalencyMap
			|| !this.equivalencyMap.TransferMap)) {
			await this.runEquivalencyMap();
		}
	}

}
</script>
